<template>
  <a-modal
    :visible="visible"
    title="Discipline Record Details"
    :footer="null"
    @cancel="handleCancel"
    width="750px"
  >
    <a-spin :spinning="loading">
      <div v-if="record" class="discipline-details">

        <!-- Student Information -->
        <a-card title="Student Information" size="small" class="mb-3">
          <a-table
            :columns="infoColumns"
            :data-source="studentInfo"
            :pagination="false"
            size="small"
            rowKey="label"
            bordered
          />
        </a-card>

        <!-- Incident Information -->
        <a-card title="Incident Information" size="small" class="mb-3">
          <a-table
            :columns="infoColumns"
            :data-source="incidentInfo"
            :pagination="false"
            size="small"
            rowKey="label"
            bordered
          >
            <template slot="value" slot-scope="text, row">
              <span v-if="row.type === 'severity'">
                <a-tag :color="getSeverityColor(row.value)">
                  {{ row.value | capitalize }}
                </a-tag>
              </span>

              <span v-else-if="row.type === 'description'">
                <div class="description-text">{{ row.value }}</div>
              </span>

              <span v-else>
                {{ row.value }}
              </span>
            </template>
          </a-table>
        </a-card>

        <!-- Penalty Information -->
        <a-card v-if="record.penalty" title="Penalty Information" size="small" class="mb-3">
          <a-table
            :columns="infoColumns"
            :data-source="penaltyInfo"
            :pagination="false"
            size="small"
            rowKey="label"
            bordered
          >
            <template slot="value" slot-scope="text, row">
              <span v-if="row.type === 'severity'">
                <a-tag :color="getSeverityColor(row.value)">
                  {{ row.value | capitalize }}
                </a-tag>
              </span>
              <span v-else>{{ row.value }}</span>
            </template>
          </a-table>
        </a-card>

        <!-- Reporting Information -->
        <a-card title="Reporting Information" size="small">
          <a-table
            :columns="infoColumns"
            :data-source="reportInfo"
            :pagination="false"
            size="small"
            rowKey="label"
            bordered
          />
        </a-card>

      </div>

      <div v-else-if="!loading" class="text-center p-4">
        <a-empty description="No record found" />
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import moment from 'moment'
import {
  Modal,
  Spin,
  Card,
  Tag,
  Table,
  Empty,
  notification,
} from 'ant-design-vue'

export default {
  name: 'ViewDisciplineModal',
  components: {
    'a-modal': Modal,
    'a-spin': Spin,
    'a-card': Card,
    'a-tag': Tag,
    'a-table': Table,
    'a-empty': Empty,
  },

  props: {
    visible: Boolean,
    recordId: [String, Number],
  },

  data() {
    return {
      loading: false,
      record: null,

      infoColumns: [
        {
          title: 'Label',
          dataIndex: 'label',
          key: 'label',
          width: '40%',
        },
        {
          title: 'Information',
          dataIndex: 'value',
          key: 'value',
          scopedSlots: { customRender: 'value' },
        },
      ],
    }
  },

  watch: {
    visible(val) {
      if (val && this.recordId) {
        this.fetchRecordDetails()
      } else {
        this.record = null
      }
    },
  },

  computed: {
    /* ---------------- STUDENT INFO TABLE ---------------- */
    studentInfo() {
      if (!this.record || !this.record.student) return []

      return [
        {
          label: 'Student Name',
          value:
            this.record.student.first_name +
              ' ' +
              this.record.student.last_name || '-',
        },
        {
          label: 'Student Code',
          value: this.record.student.student_code || '-',
        },
        {
          label: 'Class',
          value: this.getClassName(),
        },
        {
          label: 'Section',
          value: this.getSectionName(),
        },
      ]
    },

    /* ---------------- INCIDENT INFO ---------------- */
    incidentInfo() {
      if (!this.record) return []
      return [
        { label: 'Incident Date', value: this.formatDate(this.record.incident_date) },
        { label: 'Category', value: this.record.category.name },
        { label: 'Severity', value: this.record.severity, type: 'severity' },
        { label: 'Description', value: this.record.description, type: 'description' },
      ]
    },

    /* ---------------- PENALTY INFO ---------------- */
    penaltyInfo() {
      if (!this.record || !this.record.penalty) return []
      const arr = [
        { label: 'Penalty Type', value: this.record.penalty.name },
      ]

      if (this.record.penalty_details) {
        arr.push({
          label: 'Penalty Details',
          value: this.record.penalty_details,
        })
      }

      arr.push({
        label: 'Penalty Severity',
        value: this.record.penalty.severity,
        type: 'severity',
      })

      return arr
    },

    /* ---------------- REPORT INFO ---------------- */
    reportInfo() {
      if (!this.record || !this.record.reported_by) {
        return [
          { label: 'Reported By', value: '-' },
          { label: 'Reported Date', value: '-' },
          { label: 'Last Updated', value: '-' },
        ]
      }
      return [
        {
          label: 'Reported By',
          value:
            this.record.reported_by.first_name +
            ' ' +
            this.record.reported_by.last_name,
        },
        {
          label: 'Reported Date',
          value: this.formatDateTime(this.record.created_at),
        },
        {
          label: 'Last Updated',
          value:
            this.record.updated_at === this.record.created_at
              ? '-'
              : this.formatDateTime(this.record.updated_at),
        },
      ]
    },
  },

  methods: {
    /* ---------------- FETCH DATA ---------------- */
    async fetchRecordDetails() {
      this.loading = true
      try {
        const response = await this.$http.get(
          '/api/student-discipline/' + this.recordId
        )
        this.record = response.data.data
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'Failed to load discipline record details',
        })
      } finally {
        this.loading = false
      }
    },

    /* ---------------- CLASS NAME ---------------- */
    getClassName() {
      if (
        !this.record ||
        !this.record.student ||
        !this.record.student.enrollment ||
        !this.record.student.enrollment.length ||
        !this.record.student.enrollment[0] ||
        !this.record.student.enrollment[0].school_class
      ) {
        return '-'
      }

      return this.record.student.enrollment[0].school_class.name || '-'
    },

    /* ---------------- SECTION NAME ---------------- */
    getSectionName() {
      if (
        !this.record ||
        !this.record.student ||
        !this.record.student.enrollment ||
        !this.record.student.enrollment.length ||
        !this.record.student.enrollment[0] ||
        !this.record.student.enrollment[0].section
      ) {
        return '-'
      }

      return this.record.student.enrollment[0].section.name || '-'
    },

    /* ---------------- HELPERS ---------------- */
    getSeverityColor(severity) {
      const colors = {
        minor: 'orange',
        medium: 'blue',
        major: 'red',
      }
      return colors[severity] || 'default'
    },

    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '-'
    },

    formatDateTime(dateTime) {
      return dateTime ? moment(dateTime).format('YYYY-MM-DD HH:mm') : '-'
    },

    handleCancel() {
      this.$emit('close')
    },
  },

  filters: {
    capitalize(value) {
      if (!value) return ''
      value = value.toString()
      return value.charAt(0).toUpperCase() + value.slice(1)
    },
  },
}
</script>

<style scoped>
.discipline-details {
  max-height: 70vh;
  overflow-y: auto;
}

.description-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}

.mb-3 {
  margin-bottom: 16px;
}

.text-center {
  text-align: center;
}

.p-4 {
  padding: 16px;
}
</style>
